class CartDrawer extends HTMLElement {
  constructor() {
    super();
    window.FoxTheme = window.FoxTheme || {};
    window.FoxTheme.Cart = this;
    this.drawer = this.closest("drawer-component");
    window.FoxTheme.CartDrawer = this.drawer;
    this.openWhenAdded = this.dataset.openWhenAdded === "true";

    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.onUpsellPointerDownListener = this.onUpsellPointerDown.bind(this);
    this.onUpsellPointerMoveListener = this.onUpsellPointerMove.bind(this);
    this.onUpsellPointerUpListener = this.onUpsellPointerUp.bind(this);
    this.onUpsellPointerCancelListener = this.onUpsellPointerCancel.bind(this);
    this.onUpsellClickListener = this.onUpsellClick.bind(this);
    this.upsellDragState = null;
    this.suppressUpsellClick = false;
    this.protectionObserver = null;
    this.cartUpdateUnsubscriber = null;
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber?.();
    this.cartUpdateUnsubscriber = window.FoxThemeEvents.subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      async (state) => {
        if (state.item_count !== 0) {
          this.renderContents(
            state,
            true,
            state.source === "quick-order-list" ? false : true
          );
        }
      }
    );

    document.addEventListener("cart:refresh", this.onCartRefreshListener);

    // FISOEW cart-only: enable mouse drag scrolling for the upsell rail on desktop.
    this.addEventListener("pointerdown", this.onUpsellPointerDownListener);
    this.addEventListener("pointermove", this.onUpsellPointerMoveListener);
    this.addEventListener("pointerup", this.onUpsellPointerUpListener);
    this.addEventListener("pointercancel", this.onUpsellPointerCancelListener);
    this.addEventListener("click", this.onUpsellClickListener, true);

    // FISOEW cart-only: keep third-party Worry-Free Delivery inside the
    // scrollable cart details area while checkout remains the only fixed footer.
    this.setupProtectionRelocation();
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
    this.cartUpdateUnsubscriber = null;
    document.removeEventListener("cart:refresh", this.onCartRefreshListener);
    this.removeEventListener("pointerdown", this.onUpsellPointerDownListener);
    this.removeEventListener("pointermove", this.onUpsellPointerMoveListener);
    this.removeEventListener("pointerup", this.onUpsellPointerUpListener);
    this.removeEventListener("pointercancel", this.onUpsellPointerCancelListener);
    this.removeEventListener("click", this.onUpsellClickListener, true);
    this.protectionObserver?.disconnect();
    this.protectionObserver = null;
  }

  setupProtectionRelocation() {
    const footer = this.querySelector(".f-drawer__footer");
    if (!footer) return;

    this.relocateProtectionWidget();
    this.protectionObserver?.disconnect();
    this.protectionObserver = new MutationObserver(() => {
      this.relocateProtectionWidget();
    });
    this.protectionObserver.observe(footer, { childList: true, subtree: true });

    // App embeds can render asynchronously after the drawer is initialized.
    [0, 250, 750, 1500].forEach((delay) => {
      window.setTimeout(() => this.relocateProtectionWidget(), delay);
    });
  }

  relocateProtectionWidget() {
    const slot = this.querySelector("[data-cart-protection-slot]");
    const footer = this.querySelector(".f-drawer__footer");
    if (!slot || !footer) return;

    const checkoutButton = footer.querySelector("[data-checkout-button]");
    const candidates = Array.from(footer.querySelectorAll("*"));
    const widgetPart = candidates.find((element) => {
      if (checkoutButton && element.contains(checkoutButton)) return false;

      const className =
        typeof element.className === "string" ? element.className : "";
      const signature = `${element.id || ""} ${className} ${
        element.getAttribute("data-app") || ""
      } ${element.getAttribute("data-testid") || ""}`;
      const text = (element.textContent || "").trim();

      return (
        /seel|worry[-_\s]?free|shipping[-_\s]?protection|delivery[-_\s]?protection/i.test(
          signature
        ) || /worry[-\s]?free delivery/i.test(text)
      );
    });

    if (!widgetPart) return;

    let widget = widgetPart;
    while (widget.parentElement && widget.parentElement !== footer) {
      const parent = widget.parentElement;
      if (checkoutButton && parent.contains(checkoutButton)) break;
      if (
        parent.matches(".f-cart-drawer__buttons, form#CartDrawer")
      ) {
        break;
      }
      widget = parent;
    }

    if (checkoutButton && widget.contains(checkoutButton)) return;
    slot.appendChild(widget);
  }

  onUpsellPointerDown(event) {
    if (event.pointerType !== "mouse" || event.button !== 0) return;

    const scroller = event.target.closest(".f-cart-upsell__items");
    if (!scroller || !this.contains(scroller)) return;

    // Keep Add/option controls clickable; links/images/titles remain draggable.
    if (
      event.target.closest(
        ".f-cart-upsell__add, button, input, select, textarea, product-form"
      )
    ) {
      return;
    }

    this.upsellDragState = {
      scroller,
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: scroller.scrollLeft,
      moved: false,
    };

    scroller.classList.add("is-dragging");
    if (scroller.setPointerCapture) {
      scroller.setPointerCapture(event.pointerId);
    }
  }

  onUpsellPointerMove(event) {
    const state = this.upsellDragState;
    if (!state || event.pointerId !== state.pointerId) return;

    const deltaX = event.clientX - state.startX;
    if (Math.abs(deltaX) > 4) {
      state.moved = true;
      event.preventDefault();
    }

    if (state.moved) {
      state.scroller.scrollLeft = state.startScrollLeft - deltaX;
    }
  }

  onUpsellPointerUp(event) {
    const state = this.upsellDragState;
    if (!state || event.pointerId !== state.pointerId) return;

    if (
      state.scroller.releasePointerCapture &&
      state.scroller.hasPointerCapture?.(event.pointerId)
    ) {
      state.scroller.releasePointerCapture(event.pointerId);
    }
    state.scroller.classList.remove("is-dragging");

    if (state.moved) {
      this.suppressUpsellClick = true;
      window.setTimeout(() => {
        this.suppressUpsellClick = false;
      }, 0);
    }

    this.upsellDragState = null;
  }

  onUpsellPointerCancel(event) {
    const state = this.upsellDragState;
    if (!state || event.pointerId !== state.pointerId) return;

    state.scroller.classList.remove("is-dragging");
    this.upsellDragState = null;
  }

  onUpsellClick(event) {
    if (!this.suppressUpsellClick) return;
    if (!event.target.closest(".f-cart-upsell__items a")) return;

    event.preventDefault();
    event.stopPropagation();
  }

  async onCartRefresh(event) {
    const cartDrawer = document.getElementById("cart-drawer");
    if (!cartDrawer) return;

    try {
      const response = await fetch(
        `${FoxThemeSettings.routes.root}?section_id=cart-drawer`
      );
      const responseText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, "text/html");

      const refreshedCart = parsedHTML.getElementById("cart-drawer");
      if (!refreshedCart) return;

      const newCartContent = refreshedCart.innerHTML;
      cartDrawer.innerHTML = newCartContent;

      setTimeout(() => {
        const newDrawer = cartDrawer.querySelector("drawer-component");
        if (event.detail?.open === true && !newDrawer?.isOpen()) {
          newDrawer?.openDrawer();
        }
      }, 100);

      // Update cart count
      FoxThemeCartHelpers.getState().then((state) => {
        FoxThemeCartHelpers.updateCartCount(state.item_count);
      });
    } catch (error) {
      console.error("Error refreshing cart:", error);
    }
  }

  renderContents(parsedState, renderFooter = true, isOpen = true) {
    if (parsedState.errors) return;
    this.classList.contains("is-empty") && this.classList.remove("is-empty");
    this.getSectionsToRender(renderFooter).forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHTML = parsedState.sections?.[section.id];
      if (!sectionElement || !sectionHTML) return;

      const replacementHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );
      if (replacementHTML !== null) {
        sectionElement.innerHTML = replacementHTML;
      }
    });

    setTimeout(() => {
      if (this.openWhenAdded && !this.drawer.isOpen() && isOpen) {
        this.drawer.openDrawer();
        document.body.classList.add("prevent-scroll");
        window.FoxThemeEvents.emit(`ON_CART_DRAWER_OPEN`, parsedState);
      }
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const element = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender(renderFooter = false) {
    const sections = [
      {
        id: "cart-drawer",
        selector: ".f-cart-drawer__items",
      },
    ];

    if (renderFooter) {
      sections.push(
        {
          id: "cart-drawer",
          selector: ".f-cart-drawer__details-content",
        },
        {
          id: "cart-drawer",
          selector: ".f-drawer__footer",
        }
      );
    }

    return sections;
  }
}
customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  initLoading() {
    this.loading = new window.FoxTheme.AnimateLoading(
      document.querySelector("cart-drawer"),
      { overlay: document.querySelector("cart-drawer") }
    );
  }
  getSectionsToRender() {
    return [
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__items",
      },
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__block-subtotal",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
